import { z } from 'zod/v4';

import { api } from '@/lib/api';

export const onboardingServerStepSchema = z.enum(['personalize', 'connect']);
export const onboardingServerGoalSchema = z.enum(['publish', 'comments', 'analytics', 'ai', 'ugc', 'webhooks']);
export const onboardingServerBuildModeSchema = z.enum(['code', 'agent', 'both']);
export const onboardingServerCheckoutStatusSchema = z.enum(['none', 'redirected', 'completed', 'canceled']);

export const onboardingStateResponseSchema = z.object({
    shouldShow: z.boolean(),
    completed: z.boolean(),
    currentStep: onboardingServerStepSchema,
    goals: z.array(onboardingServerGoalSchema),
    buildMode: onboardingServerBuildModeSchema.nullable(),
    sources: z.int(),
    connectSkipped: z.boolean(),
    connectedAccounts: z.int(),
    checkoutStatus: onboardingServerCheckoutStatusSchema,
    seenAt: z.string().nullable(),
    completedAt: z.string().nullable()
});

export type OnboardingServerStep = z.infer<typeof onboardingServerStepSchema>;
export type OnboardingServerGoal = z.infer<typeof onboardingServerGoalSchema>;
export type OnboardingServerBuildMode = z.infer<typeof onboardingServerBuildModeSchema>;
export type OnboardingServerCheckoutStatus = z.infer<typeof onboardingServerCheckoutStatusSchema>;
export type OnboardingStateResponse = z.infer<typeof onboardingStateResponseSchema>;

/**
 * Partial update payload. `checkoutStatus` deliberately excludes `completed`:
 * completion is webhook-driven on the backend and client writes of
 * `completed` are rejected with a 400.
 */
export type OnboardingStateUpdateInput = {
    goals?: OnboardingServerGoal[];
    buildMode?: OnboardingServerBuildMode | null;
    sources?: number;
    connectSkipped?: boolean;
    currentStep?: OnboardingServerStep;
    checkoutStatus?: Exclude<OnboardingServerCheckoutStatus, 'completed'>;
    seenAt?: string;
};

export async function getOnboardingState(organizationId: string): Promise<OnboardingStateResponse> {
    const response = await api.get(`/v1/organizations/${organizationId}/onboarding/state`, { authenticated: true });
    return onboardingStateResponseSchema.parse(response);
}

export async function updateOnboardingState(
    organizationId: string,
    input: OnboardingStateUpdateInput
): Promise<OnboardingStateResponse> {
    const response = await api.patch(`/v1/organizations/${organizationId}/onboarding/state`, input, {
        authenticated: true
    });
    return onboardingStateResponseSchema.parse(response);
}
